import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatLayoutMobile from './components/ChatLayoutMobile';
import ChatLayoutDesktop from './components/ChatLayoutDesktop';
import useChatWebSocket from './hooks/useChatWebSocket';
import useChatMessages from './hooks/useChatMessages';
import useChatStreaming from './hooks/useChatStreaming';
import useChatTheme from './hooks/useChatTheme';
import useChatAuthorization from './hooks/useChatAuthorization';
import useWhatsAppLink from './hooks/useWhatsAppLink';
import useIsMobile from './hooks/useIsMobile';
import useChatConsent from './hooks/useChatConsent';
import ConsentOverlay from './components/ConsentOverlay';
import { ModelService } from '@/services/ModelService';
import { parseWebSocketMessage, buildSendPayload } from './hooks/chatMessageUtils';

const ChatPage = () => {
    const { t } = useTranslation('chat');
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const sid = searchParams.get('sid');
    const rid = searchParams.get('rid');

    const isMobile = useIsMobile();

    const isShortCode = token?.length === 8;
    const secretKey = isShortCode ? null : (token ?? null);
    const shortCode = isShortCode ? token : null;

    const { status: authStatus, authData, retry } = useChatAuthorization({
        secretKey,
        shortCode,
        scenarioId: sid,
        workflowId: rid,
    });

    const [isDark, setIsDark] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const jobTitle = authData?.jobTitle ?? '';
    const [hasShadow, setHasShadow] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [agentName, setAgentName] = useState(null);

    const colors = useChatTheme(isDark);

    useEffect(() => {
        const html = document.documentElement;
        const { body } = document;
        const saved = {
            htmlBackground: html.style.background,
            htmlOverflow: html.style.overflow,
            background: body.style.background,
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            bottom: body.style.bottom,
            width: body.style.width,
            height: body.style.height,
            overscrollBehavior: body.style.overscrollBehavior,
        };
        html.style.background = colors.chatBg;
        html.style.overflow = 'hidden';
        Object.assign(body.style, {
            background: colors.chatBg,
            overflow: 'hidden',
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            width: '100%',
            height: '100%',
            overscrollBehavior: 'none',
        });
        return () => {
            html.style.background = saved.htmlBackground;
            html.style.overflow = saved.htmlOverflow;
            Object.assign(body.style, {
                background: saved.background,
                overflow: saved.overflow,
                position: saved.position,
                top: saved.top,
                left: saved.left,
                right: saved.right,
                bottom: saved.bottom,
                width: saved.width,
                height: saved.height,
                overscrollBehavior: saved.overscrollBehavior,
            });
        };
    }, [colors.chatBg]);

    const directName = authData?.agentName ?? authData?.botName ?? null;
    const agentId = authData?.agentId ?? authData?.modelId ?? null;

    useEffect(() => {
        if (directName || !agentId) {
            setAgentName(null);
            return undefined;
        }
        let cancelled = false;
        ModelService.getById(agentId)
            .then(agent => { if (!cancelled) setAgentName(agent?.name ?? null); })
            .catch(() => { if (!cancelled) setAgentName(null); });
        return () => { cancelled = true; };
    }, [directName, agentId]);

    const botName = directName || agentName || t('header.assistantFallback');

    const { messages, isBotTyping, appendMessage, popLastMessage, setBotTyping, mergeEntities } = useChatMessages();
    const reconnectSkipCountRef = useRef(0);
    const historyReplayCountRef = useRef(0);
    const [pendingUserMessage, setPendingUserMessage] = useState(null);
    const [hasBotSentFirstMessage, setHasBotSentFirstMessage] = useState(false);
    const { linkWhatsApp } = useWhatsAppLink();

    const { streamingState, startStream } = useChatStreaming({
        onStreamComplete: (finalMessage, { isLast } = {}) => {
            appendMessage(finalMessage);
            if (isLast) {
                setBotTyping(false);
                setIsSending(false);
            }
        },
    });

    const handleRawMessage = useCallback((rawData) => {
        const parsed = parseWebSocketMessage(rawData, { ignoreSpecial: true });
        console.log('[handleRawMessage] parsed:', parsed);

        if (parsed.conversationId) {
            setConversationId(parsed.conversationId);
        }

        switch (parsed.type) {
            case 'stream':
                console.log('[Chat] 🎬 stream start');
                setHasBotSentFirstMessage(true);
                setPendingUserMessage(null);
                setBotTyping(false);
                startStream({ text: parsed.text, endConversation: parsed.endConversation === true });
                break;
            case 'history':
                setHasBotSentFirstMessage(true);
                if (reconnectSkipCountRef.current > 0) {
                    historyReplayCountRef.current++;
                    if (historyReplayCountRef.current > reconnectSkipCountRef.current) {
                        setBotTyping(false);
                        setIsSending(false);
                        setPendingUserMessage(null);
                        appendMessage(parsed.message);
                    }
                } else {
                    appendMessage(parsed.message);
                }
                break;
            case 'history_api':
                setHasBotSentFirstMessage(true);
                if (reconnectSkipCountRef.current > 0) {
                    historyReplayCountRef.current++;
                    if (historyReplayCountRef.current > reconnectSkipCountRef.current) {
                        setBotTyping(false);
                        setIsSending(false);
                        setPendingUserMessage(null);
                        popLastMessage();
                        appendMessage(parsed.message);
                    }
                } else {
                    popLastMessage();
                    appendMessage(parsed.message);
                }
                break;
            case 'api':
                console.log('[Chat] ✅ api response received');
                setHasBotSentFirstMessage(true);
                setPendingUserMessage(null);
                setBotTyping(false);
                setIsSending(false);
                appendMessage(parsed.message);
                break;
            case 'entities':
                mergeEntities(parsed.entities);
                break;
            default:
                break;
        }
    }, [appendMessage, popLastMessage, setBotTyping, mergeEntities, startStream]);

    const wsSid = sid || authData?.scenarioId;
    const wsRid = rid || authData?.recruitmentId;
    const effectiveConversationId = conversationId ?? authData?.conversationId ?? null;

    const handleReconnect = useCallback(() => {
        if (messages.length > 0) {
            reconnectSkipCountRef.current = messages.length;
            historyReplayCountRef.current = 0;
        }
    }, [messages]);

    const { send, isConnected, reconnectState } = useChatWebSocket({
        sid: wsSid,
        rid: wsRid,
        cid: effectiveConversationId,
        onMessage: handleRawMessage,
        onReconnect: handleReconnect,
        enabled: authStatus === 'authorized',
    });

    const handleSendMessage = useCallback((message) => {
        const payload = buildSendPayload(message);
        if (!payload) return;

        console.log('[Chat] 📨 sending', message.type, message.text?.slice(0, 80));

        const newMsg = {
            id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
            type: message.type,
            text: message.text,
            filename: message.filename,
            content: message.content,
            user: 'user',
            timestamp: new Date().toISOString(),
        };
        setPendingUserMessage(newMsg);
        appendMessage(newMsg);

        send(payload);
        setBotTyping(true);
        setIsSending(true);
        console.log('[Chat] isSending=true, botTyping=true');
    }, [send, appendMessage, setBotTyping]);

    const handleFileSelect = useCallback((fileData) => {
        handleSendMessage(fileData);
    }, [handleSendMessage]);

    const handleWhatsUpNo = useCallback(() => {
        handleSendMessage({ type: 'text', text: 'No' });
    }, [handleSendMessage]);

    const handleWhatsUpYes = useCallback((phone, onSuccess) => {
        linkWhatsApp(phone, conversationId)
            .then(() => onSuccess && onSuccess())
            .catch(err => console.error('Failed to link WhatsApp', err));
    }, [linkWhatsApp, conversationId]);

    const handleScroll = (e) => {
        setHasShadow(e.target.scrollTop > 0);
    };

    const recruitmentId = authData?.recruitmentId ?? rid ?? null;
    const consent = useChatConsent({
        recruitmentId,
        conversationId: effectiveConversationId,
        enabled: authStatus === 'authorized',
    });

    const isReady = authStatus === 'authorized' && hasBotSentFirstMessage;

    const Layout = isMobile ? ChatLayoutMobile : ChatLayoutDesktop;

    return (
        <>
            <Layout
                colors={colors}
                authStatus={authStatus}
                isReady={isReady}
                onRetry={retry}
                header={{
                    isDark,
                    onToggleDark: () => setIsDark(d => !d),
                    botName,
                    jobTitle,
                    hasShadow,
                    linkWhatsApp,
                    conversationId,
                }}
                messageListProps={{
                    messages,
                    pendingUserMessage,
                    isBotTyping,
                    streamingState,
                    onScroll: handleScroll,
                    onWhatsUpYes: handleWhatsUpYes,
                    onWhatsUpNo: handleWhatsUpNo,
                    reconnectState,
                    onItemPress: (text) => handleSendMessage({ type: 'text', text }),
                }}
                inputProps={{
                    onSendMessage: handleSendMessage,
                    isSending,
                    onFileSelect: handleFileSelect,
                }}
            />
            {authStatus === 'authorized' && recruitmentId && !consent.isAccepted && (
                <ConsentOverlay
                    colors={colors}
                    settings={consent.settings}
                    isLoading={consent.isLoading || !consent.settings}
                    recruitmentId={recruitmentId}
                    onAccept={consent.accept}
                />
            )}
        </>
    );
};

export default ChatPage;
